import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';
import { ShoppingCart, Plus, Minus, Clock, ChevronRight, ChevronLeft, Search, MapPin, User, Phone, FileText, Navigation, Tag, RotateCcw, ChevronDown, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Button } from '../components/ui/Button';
import {
  clearPendingOrderSubmission,
  loadPendingOrderSubmission,
  PendingOrderSubmission,
  savePendingOrderSubmission,
} from '../utils/pendingOrderSubmission';
import { Card } from '../components/ui/Card';
import { Skeleton } from '../components/ui/Skeleton';
import { useToast } from '../components/ui/Toast';
import { BrandLogo } from '../components/BrandLogo';
import { ThemeToggle } from '../components/ThemeToggle';
import { resolveProductImageUrl } from '../utils/productImage';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const DEFAULT_CENTER: [number, number] = [6.1701, -75.5910]; // Envigado, Colombia

interface OrderHistoryEntry {
  items: Array<{ productId: string; name: string; quantity: number; price: number; selectedOptions: Record<string, string> }>;
  delivery: DeliveryData;
  date: string;
  total: number;
  orderNumber?: number;
  paymentMethod?: string;
  paymentStatus?: string;
  paymentInstructions?: string;
  orderStatus?: string;
}

const HISTORY_KEY = 'choripan_order_history';
const MAX_HISTORY = 5;
// Explicit retention policy: entries older than this never come back, even if
// the count limit hasn't been reached.
const RETENTION_DAYS = 7;

const isValidHistoryEntry = (entry: any): entry is OrderHistoryEntry =>
  entry && typeof entry === 'object'
  && Array.isArray(entry.items)
  && entry.items.every((item: any) => item && typeof item.productId === 'string' && Number.isFinite(item.quantity))
  && typeof entry.date === 'string' && !Number.isNaN(new Date(entry.date).getTime())
  && Number.isFinite(entry.total);

const saveOrderToHistory = (order: OrderHistoryEntry) => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    const history: OrderHistoryEntry[] = raw ? JSON.parse(raw) : [];
    history.unshift(order);
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
  } catch { /* localStorage may be unavailable */ }
};

const loadOrderHistory = (): OrderHistoryEntry[] => {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    const cutoff = Date.now() - RETENTION_DAYS * 24 * 60 * 60 * 1000;
    return parsed
      .filter(isValidHistoryEntry)
      .filter(entry => new Date(entry.date).getTime() >= cutoff);
  } catch { return []; }
};

const clearOrderHistory = () => {
  try { localStorage.removeItem(HISTORY_KEY); } catch { /* unavailable */ }
};

// Recoverable checkout draft: survives a refresh, expires after 24h, and is
// cleared once the order is accepted. Coordinates are never persisted.
const DRAFT_KEY = 'choripan_checkout_draft';
const DRAFT_TTL_HOURS = 24;
const LANGUAGE_KEY = 'choripan_customer_language';
type CustomerLanguage = 'es' | 'en';

const readCustomerLanguage = (): CustomerLanguage => {
  try { return localStorage.getItem(LANGUAGE_KEY) === 'en' ? 'en' : 'es'; }
  catch { return 'es'; }
};

const localizeStatus = (value: string, language: CustomerLanguage) => {
  if (language === 'es') return value;
  const translations: Record<string, string> = {
    Recibido: 'Received', pendiente: 'Pending preparation', Pendiente: 'Pending preparation', preparando: 'Preparing', Preparando: 'Preparing',
    listo: 'Ready', Listo: 'Ready', entregado: 'Delivered', Entregado: 'Delivered', cancelado: 'Canceled', Cancelado: 'Canceled',
    'Pendiente de verificación': 'Awaiting verification',
    'Pendiente al recibir el pedido': 'Due on delivery', 'Estado pendiente de confirmación': 'Awaiting confirmation'
  };
  return translations[value] || value;
};

const localizePayment = (value: string, language: CustomerLanguage) => {
  if (language === 'es') return value;
  if (value === 'Efectivo') return 'Cash';
  if (value === 'Transferencia') return 'Bank transfer';
  return value;
};

const localizePaymentInstructions = (value: string, language: CustomerLanguage) => {
  if (language === 'es') return value;
  if (value.startsWith('Sigue las instrucciones de pago')) return 'Follow the restaurant’s payment instructions; this app does not make the transfer.';
  if (value.startsWith('Paga en efectivo')) return 'Pay in cash when your order arrives.';
  return value;
};

const localizeApiMessage = (value: string, language: CustomerLanguage) => {
  if (language === 'es') return value;
  const exact: Record<string, string> = {
    'Ingresa un código de cupón.': 'Enter a coupon code.',
    'Cupón no encontrado.': 'Coupon not found.',
    'Este cupón ya no está activo.': 'This coupon is no longer active.',
    'Este cupón ha expirado.': 'This coupon has expired.',
    'Este cupón ya alcanzó el límite de usos.': 'This coupon has reached its usage limit.',
    'Cupón no válido.': 'Invalid coupon.',
    'El nombre debe tener al menos 2 caracteres.': 'Name must be at least 2 characters long.',
    'La dirección debe tener al menos 5 caracteres.': 'Address must be at least 5 characters long.',
    'El precio del producto o el total del pedido es inválido.': 'The item price or order total is invalid.',
    'La configuración monetaria del cupón es inválida.': 'The coupon currency configuration is invalid.'
  };
  if (exact[value]) return exact[value];
  if (value.startsWith('Pedido mínimo de $')) return value.replace('Pedido mínimo de $', 'Minimum order of $').replace(' para usar este cupón.', ' is required to use this coupon.').replace(' para este cupón.', ' is required for this coupon.');
  if (value.startsWith('Teléfono inválido.')) return 'Invalid phone number. Enter a 10-digit Colombian mobile number (e.g. 3001234567).';
  if (value.includes('no está disponible.')) return value.replace('ya no está disponible. Actualiza el menú e intenta de nuevo.', 'is no longer available. Refresh the menu and try again.');
  if (value.startsWith('Falta seleccionar:')) return value.replace('Falta seleccionar:', 'Please select:').replace(' para ', ' for ');
  return 'We could not complete your request. Please check your details and try again.';
};

const saveCheckoutDraft = (draft: { cart: any[]; delivery: DeliveryData }) => {
  try {
    if (!draft.cart.length && !draft.delivery.customerName) {
      localStorage.removeItem(DRAFT_KEY);
      return;
    }
    localStorage.setItem(DRAFT_KEY, JSON.stringify({
      ...draft,
      delivery: { ...draft.delivery, coordinates: null },
      savedAt: new Date().toISOString()
    }));
  } catch { /* unavailable */ }
};

const loadCheckoutDraft = (): { cart: any[]; delivery?: DeliveryData } | null => {
  try {
    const raw = localStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.cart) || typeof parsed.savedAt !== 'string') return null;
    if (Date.now() - new Date(parsed.savedAt).getTime() > DRAFT_TTL_HOURS * 3600 * 1000) {
      localStorage.removeItem(DRAFT_KEY);
      return null;
    }
    return parsed;
  } catch { return null; }
};

const clearCheckoutDraft = () => {
  try { localStorage.removeItem(DRAFT_KEY); } catch { /* unavailable */ }
};

const relativeDate = (iso: string, language: CustomerLanguage): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return language === 'es' ? 'Justo ahora' : 'Just now';
  if (mins < 60) return language === 'es' ? `Hace ${mins} min` : `${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return language === 'es' ? `Hace ${hours}h` : `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return language === 'es' ? 'Ayer' : 'Yesterday';
  if (days < 7) return language === 'es' ? `Hace ${days} días` : `${days} days ago`;
  return new Date(iso).toLocaleDateString(language === 'es' ? 'es-CO' : 'en-US', { day: 'numeric', month: 'short' });
};

interface DeliveryData {
  customerName: string;
  phone: string;
  address: string;
  addressNotes: string;
  coordinates: { lat: number; lng: number } | null;
}

const PHONE_REGEX = /^3\d{9}$/;

const validateField = (name: string, value: string, language: CustomerLanguage) => {
  const spanish = language === 'es';
  switch (name) {
    case 'customerName': return value.trim().length >= 2 ? '' : spanish ? 'Mínimo 2 caracteres' : 'Enter at least 2 characters';
    case 'phone': return PHONE_REGEX.test(value.replace(/\s/g, '')) ? '' : spanish ? 'Formato: 3XX XXX XXXX (10 dígitos)' : 'Format: 3XX XXX XXXX (10 digits)';
    case 'address': return value.trim().length >= 5 ? '' : spanish ? 'Mínimo 5 caracteres' : 'Enter at least 5 characters';
    default: return '';
  }
};

const formatPhone = (raw: string) => {
  const digits = raw.replace(/\D/g, '').slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
};

const DraggableMarker = ({ position, onMove }: { position: [number, number]; onMove: (lat: number, lng: number) => void }) => {
  const markerRef = useRef<L.Marker>(null);
  useMapEvents({
    click(e) { onMove(e.latlng.lat, e.latlng.lng); }
  });
  return (
    <Marker
      position={position}
      draggable
      ref={markerRef}
      eventHandlers={{
        dragend() {
          const m = markerRef.current;
          if (m) { const p = m.getLatLng(); onMove(p.lat, p.lng); }
        }
      }}
    />
  );
};

const RecenterMap = ({ lat, lng }: { lat: number; lng: number }) => {
  const map = useMap();
  useEffect(() => { map.setView([lat, lng], map.getZoom()); }, [lat, lng, map]);
  return null;
};

const MenuQR = () => {
  const { showToast } = useToast();
  const [language, setLanguage] = useState<CustomerLanguage>(readCustomerLanguage);
  const tr = (spanish: string, english: string) => language === 'es' ? spanish : english;
  const languageRef = useRef(language);
  const [products, setProducts] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [menuError, setMenuError] = useState(false);
  const [cart, setCart] = useState<any[]>([]);
  const pendingSubmissionRef = useRef<PendingOrderSubmission | null>(null);
  const [pendingSubmissionReady, setPendingSubmissionReady] = useState(false);
  const [pendingSubmissionStorageError, setPendingSubmissionStorageError] = useState(false);
  // Honest connectivity: the app is online-only; never silently queue orders.
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [orderReceipt, setOrderReceipt] = useState<{ orderNumber?: number; total: number; paymentMethod: string; orderStatus: string } | null>(null);
  const receiptHeadingRef = useRef<HTMLHeadingElement>(null);
  const [activeCategory, setActiveCategory] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedOptions, setSelectedOptions] = useState<Record<string, Record<string, string>>>({});
  const [builderProduct, setBuilderProduct] = useState<any | null>(null);
  const [builderSelections, setBuilderSelections] = useState<Record<string, string>>({});

  const [checkoutStep, setCheckoutStep] = useState(1);
  const [deliveryData, setDeliveryData] = useState<DeliveryData>({
    customerName: '', phone: '', address: '', addressNotes: '', coordinates: null
  });
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const customerNameInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [mapCenter, setMapCenter] = useState<[number, number]>(DEFAULT_CENTER);
  const [markerPos, setMarkerPos] = useState<[number, number]>(DEFAULT_CENTER);
  const [geoLoading, setGeoLoading] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState<any[]>([]);
  const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);

  // Coupon state
  const [showCouponField, setShowCouponField] = useState(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState<{ code: string; discount: number; description: string } | null>(null);

  // Order history state
  const [orderHistory, setOrderHistory] = useState<OrderHistoryEntry[]>([]);
  const [showHistory, setShowHistory] = useState(true);

  useEffect(() => {
    if (orderReceipt) receiptHeadingRef.current?.focus();
  }, [orderReceipt]);

  useEffect(() => {
    languageRef.current = language;
    document.documentElement.lang = language === 'es' ? 'es' : 'en';
    try { localStorage.setItem(LANGUAGE_KEY, language); } catch { /* preference is optional */ }
  }, [language]);

  useEffect(() => {
    if (!Object.values(touched).some(Boolean)) return;
    setFieldErrors(prev => ({
      ...prev,
      ...(touched.customerName ? { customerName: validateField('customerName', deliveryData.customerName, language) } : {}),
      ...(touched.phone ? { phone: validateField('phone', deliveryData.phone.replace(/\s/g, ''), language) } : {}),
      ...(touched.address ? { address: validateField('address', deliveryData.address, language) } : {})
    }));
  }, [language, touched, deliveryData.customerName, deliveryData.phone, deliveryData.address]);

  const categories = useMemo(
    () => ['Todos', ...Array.from(new Set(products.map((p: any) => p.category).filter(Boolean)))],
    [products]
  );

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/products/menu`);
      // Transformar productos para incluir opciones si no existen
      const transformedProducts = response.data.map((p: any) => ({
        ...p,
        options: p.options || []
      }));
      setProducts(transformedProducts || []);
      setMenuError(false);
    } catch (error) {
      console.error('Error fetching products:', error);
      setMenuError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProducts();
    const history = loadOrderHistory();
    setOrderHistory(history);
    // Returning customer: pre-fill the delivery form from their most recent
    // order so they don't have to retype name/phone/address every time.
    const lastDelivery = history[0]?.delivery;
    if (lastDelivery) {
      setDeliveryData(lastDelivery);
    }
    // Recover an in-progress draft (cart + delivery form) saved before a
    // refresh, under the draft retention policy.
    let pendingSubmission: PendingOrderSubmission | null = null;
    try {
      pendingSubmission = loadPendingOrderSubmission();
      pendingSubmissionRef.current = pendingSubmission;
    } catch (error) {
      console.error('Could not restore pending order submission:', error);
      setPendingSubmissionStorageError(true);
    }
    const draft = loadCheckoutDraft();
    if (pendingSubmission) {
      setCart(pendingSubmission.cartSnapshot);
      setDeliveryData(prev => ({ ...prev, ...pendingSubmission.deliverySnapshot, coordinates: pendingSubmission!.deliverySnapshot.coordinates ?? null }));
      if (pendingSubmission.couponSnapshot) {
        setAppliedCoupon(pendingSubmission.couponSnapshot);
        setCouponInput(pendingSubmission.couponSnapshot.code);
      }
      showToast(languageRef.current === 'es' ? 'Hay un pedido pendiente. Reabrimos el mismo envío para confirmar su resultado.' : 'There is a pending order. We reopened the same submission so you can confirm its result.', 'info');
    } else if (draft) {
      if (Array.isArray(draft.cart) && draft.cart.length > 0) setCart(draft.cart);
      if (draft.delivery) setDeliveryData(prev => ({ ...prev, ...draft.delivery, coordinates: draft.delivery.coordinates ?? null }));
      if (draft.cart?.length > 0) showToast(languageRef.current === 'es' ? 'Recuperamos tu pedido en curso.' : 'We restored your order in progress.', 'info');
    }
    setPendingSubmissionReady(true);
  }, [fetchProducts, showToast]);

  useEffect(() => {
    if (activeCategory !== 'Todos' && !categories.includes(activeCategory)) {
      setActiveCategory('Todos');
    }
  }, [activeCategory, categories]);

  // Persist the recoverable draft whenever cart or delivery data changes.
  useEffect(() => {
    saveCheckoutDraft({ cart, delivery: deliveryData });
  }, [cart, deliveryData]);

  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
  }, []);

  const getDefaultSelections = (product: any) => {
    return (product.options || []).reduce((acc: Record<string, string>, opt: any) => {
      const existingChoice = selectedOptions[product._id]?.[opt.name] || product.selectedOptions?.[opt.name];
      acc[opt.name] = existingChoice || opt.choices?.[0] || '';
      return acc;
    }, {});
  };

  const openProductBuilder = (product: any) => {
    setBuilderProduct(product);
    setBuilderSelections(getDefaultSelections(product));
  };

  const closeProductBuilder = () => {
    setBuilderProduct(null);
    setBuilderSelections({});
  };

  const updateBuilderSelection = (optionName: string, choice: string) => {
    setBuilderSelections(prev => ({ ...prev, [optionName]: choice }));
  };

  const addToCart = (product: any, selectionsOverride?: Record<string, string>) => {
    if (pendingSubmissionRef.current) {
      showToast(tr('Primero confirma el resultado del pedido pendiente.', 'First confirm the result of the pending order.'), 'error');
      return;
    }
    const productSelections = selectionsOverride || product.selectedOptions || selectedOptions[product._id] || {};
    // Verificar que todas las opciones requeridas estén seleccionadas
    if (product.options && product.options.length > 0) {
      const missingOptions = product.options.filter((opt: any) => !productSelections[opt.name]);
      
      if (missingOptions.length > 0) {
        showToast(tr(`Por favor selecciona: ${missingOptions[0].name}`, `Please select: ${missingOptions[0].name}`), 'info');
        return;
      }
    }

    setCart(prev => {
      const cartItemId = `${product._id}-${JSON.stringify(productSelections)}`;
      
      const existing = prev.find(item => item.cartItemId === cartItemId);
      if (existing) {
        return prev.map(item => item.cartItemId === cartItemId ? { ...item, quantity: item.quantity + 1 } : item);
      }
      return [...prev, { ...product, cartItemId, selectedOptions: productSelections, quantity: 1 }];
    });
    showToast(tr(`${product.name} agregado`, `${product.name} added`), 'success');
  };

  const addBuilderProductToCart = () => {
    if (!builderProduct) return;

    setSelectedOptions(prev => ({
      ...prev,
      [builderProduct._id]: builderSelections
    }));
    addToCart(builderProduct, builderSelections);
    closeProductBuilder();
  };

  const getProductFallbackIcon = (product: any) => {
    if (product.category === 'Bebidas') return '🥤';
    if (product.category === 'Combos') return '🍟';
    return '🌭';
  };

  const removeFromCart = (cartItemId: string) => {
    if (pendingSubmissionRef.current) {
      showToast(tr('Primero confirma el resultado del pedido pendiente.', 'First confirm the result of the pending order.'), 'error');
      return;
    }
    setCart(prev => prev.reduce((acc, item) => {
      if (item.cartItemId === cartItemId) {
        if (item.quantity > 1) acc.push({ ...item, quantity: item.quantity - 1 });
      } else {
        acc.push(item);
      }
      return acc;
    }, [] as any[]));
  };

  const filteredProducts = products.filter((p: any) =>
    (activeCategory === 'Todos' || p.category === activeCategory) &&
    (p.name.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  // Generation counters: geocoding is slow and unordered, so only the newest
  // request may write address/coordinates state.
  const reverseGeocodeSeq = useRef(0);
  const searchSeq = useRef(0);

  const reverseGeocode = useCallback(async (lat: number, lng: number) => {
    const seq = ++reverseGeocodeSeq.current;
    try {
      const res = await axios.get(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&zoom=18&addressdetails=1`, {
        headers: { 'Accept-Language': 'es' }
      });
      if (seq !== reverseGeocodeSeq.current) return; // a newer move superseded this result
      if (res.data?.display_name) {
        setDeliveryData(prev => ({ ...prev, address: res.data.display_name, coordinates: { lat, lng } }));
        setFieldErrors(prev => ({ ...prev, address: '' }));
      }
    } catch {
      // Provider unavailable/rate-limited: keep the point usable by making the
      // text describe the exact coordinates instead of an older address.
      if (seq === reverseGeocodeSeq.current) {
        setDeliveryData(prev => ({ ...prev, address: `${lat.toFixed(6)}, ${lng.toFixed(6)}`, coordinates: { lat, lng } }));
      }
    }
  }, []);

  const handleMarkerMove = useCallback((lat: number, lng: number) => {
    // The point is the source of truth the instant it moves; the geocoded text
    // follows when it arrives.
    setDeliveryData(prev => ({ ...prev, coordinates: { lat, lng } }));
    setMarkerPos([lat, lng]);
    reverseGeocode(lat, lng);
  }, [reverseGeocode]);

  const requestGeolocation = () => {
    if (!navigator.geolocation) return;
    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const { latitude, longitude } = pos.coords;
        setMapCenter([latitude, longitude]);
        setMarkerPos([latitude, longitude]);
        reverseGeocode(latitude, longitude);
        setGeoLoading(false);
      },
      () => { setGeoLoading(false); },
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };

  const searchAddress = (query: string) => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (query.length < 4) { setAddressSuggestions([]); return; }
    searchTimeout.current = setTimeout(async () => {
      const seq = ++searchSeq.current;
      try {
        const res = await axios.get(`https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=4&countrycodes=co`, {
          headers: { 'Accept-Language': 'es' }
        });
        if (seq === searchSeq.current) {
          setAddressSuggestions(res.data || []);
          setActiveSuggestionIndex(0);
        }
      } catch { if (seq === searchSeq.current) setAddressSuggestions([]); }
    }, 500);
  };

  const selectSuggestion = (s: any) => {
    const lat = parseFloat(s.lat);
    const lng = parseFloat(s.lon);
    setDeliveryData(prev => ({ ...prev, address: s.display_name, coordinates: { lat, lng } }));
    setMarkerPos([lat, lng]);
    setMapCenter([lat, lng]);
    setAddressSuggestions([]);
    setActiveSuggestionIndex(0);
    setFieldErrors(prev => ({ ...prev, address: '' }));
  };

  const handleDeliveryChange = (field: string, value: string) => {
    const newValue = field === 'phone' ? formatPhone(value) : value;
    setDeliveryData(prev => ({ ...prev, [field]: newValue }));
    setTouched(prev => ({ ...prev, [field]: true }));
    const rawValue = field === 'phone' ? value.replace(/\s/g, '') : value;
    setFieldErrors(prev => ({ ...prev, [field]: validateField(field, rawValue, language) }));
    if (field === 'address') {
      // Manual edits invalidate any point geocoded from the previous text, so
      // the submitted address and coordinates can never describe different
      // destinations.
      setDeliveryData(prev => (prev.coordinates ? { ...prev, coordinates: null } : prev));
      searchAddress(value);
    }
  };

  const handleBlur = (field: string) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    const rawValue = field === 'phone' ? deliveryData.phone.replace(/\s/g, '') : (deliveryData as any)[field];
    setFieldErrors(prev => ({ ...prev, [field]: validateField(field, rawValue || '', language) }));
  };

  const isDeliveryValid = () => {
    return deliveryData.customerName.trim().length >= 2
      && PHONE_REGEX.test(deliveryData.phone.replace(/\s/g, ''))
      && deliveryData.address.trim().length >= 5;
  };

  const confirmDeliveryDetails = () => {
    const errors = {
      customerName: validateField('customerName', deliveryData.customerName, language),
      phone: validateField('phone', deliveryData.phone.replace(/\s/g, ''), language),
      address: validateField('address', deliveryData.address, language)
    };
    setTouched({ customerName: true, phone: true, address: true });
    setFieldErrors(prev => ({ ...prev, ...errors }));
    const firstInvalid = errors.customerName ? customerNameInputRef.current
      : errors.phone ? phoneInputRef.current
      : errors.address ? addressInputRef.current
      : null;
    if (firstInvalid) {
      window.requestAnimationFrame(() => firstInvalid.focus());
      return;
    }
    setCheckoutStep(3);
  };

  const handleAddressKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown' && addressSuggestions.length > 0) {
      event.preventDefault();
      setActiveSuggestionIndex(index => (index + 1) % addressSuggestions.length);
    } else if (event.key === 'ArrowUp' && addressSuggestions.length > 0) {
      event.preventDefault();
      setActiveSuggestionIndex(index => (index - 1 + addressSuggestions.length) % addressSuggestions.length);
    } else if (event.key === 'Enter' && addressSuggestions.length > 0) {
      event.preventDefault();
      selectSuggestion(addressSuggestions[activeSuggestionIndex] || addressSuggestions[0]);
    } else if (event.key === 'Escape' && addressSuggestions.length > 0) {
      event.preventDefault();
      setAddressSuggestions([]);
    }
  };

  const handleApplyCoupon = async () => {
    if (!couponInput.trim()) return;
    setCouponLoading(true);
    setCouponError('');
    try {
      const res = await axios.post(`${API_URL}/coupons/validate`, {
        code: couponInput.trim(),
        orderAmount: total
      });
      setAppliedCoupon({ code: res.data.code, discount: res.data.discount, description: res.data.description });
      setCouponError('');
    } catch (err: any) {
      const msg = err.response?.data?.message
        ? localizeApiMessage(err.response.data.message, language)
        : tr('Error al validar el cupón', 'Error validating coupon');
      setCouponError(msg);
      setAppliedCoupon(null);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  const finalTotal = appliedCoupon ? Math.max(0, total - appliedCoupon.discount) : total;

  const reorderFromHistory = (entry: OrderHistoryEntry) => {
    const newCart = entry.items.map(item => ({
      _id: item.productId,
      name: item.name,
      price: item.price,
      quantity: item.quantity,
      selectedOptions: item.selectedOptions,
      cartItemId: `${item.productId}-${JSON.stringify(item.selectedOptions)}`,
      options: [],
      category: '',
      description: ''
    }));
    setCart(newCart as any);
    if (entry.delivery) {
      setDeliveryData(entry.delivery);
    }
    setCheckoutStep(1);
    setIsCartOpen(true);
    showToast(tr('Pedido cargado al carrito', 'Order added to cart'), 'success');
  };

  const openCheckout = () => {
    if (pendingSubmissionRef.current) {
      setCheckoutStep(3);
      setIsCartOpen(true);
      return;
    }
    setCheckoutStep(1);
    setIsCartOpen(true);
    // Revalidate the cart against the current catalog: a price change or a
    // product disabled after the menu loaded must be reflected (and confirmed
    // to the customer) before they confirm the order.
    axios.get(`${API_URL}/products/menu`).then(res => {
      const current: any[] = res.data || [];
      let changed = false;
      setCart((prev: any[]) => {
        const next = prev.flatMap(item => {
          const live = current.find((p: any) => p._id === item._id);
          if (!live) { changed = true; return []; }
          if (live.price !== item.price) { changed = true; return [{ ...item, price: live.price }]; }
          return [item];
        });
        return next;
      });
      if (changed) showToast(tr('Actualizamos tu carrito con precios y disponibilidad actuales.', 'We updated your cart with current prices and availability.'), 'info');
    }).catch(() => { /* offline: server-side validation still protects the order */ });
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponError('');
    setShowCouponField(false);
  };

  const handlePlaceOrder = async (method: string) => {
    if (!navigator.onLine) {
      showToast(tr('Estás sin conexión. No podemos enviar el pedido; tu carrito está guardado y podrás enviarlo cuando vuelvas.', 'You are offline. We cannot submit your order; your cart is saved and you can submit it when you reconnect.'), 'error');
      return;
    }
    if (!pendingSubmissionReady || pendingSubmissionStorageError) {
      showToast(tr('No pudimos recuperar el estado seguro del pedido. No lo enviamos para evitar duplicarlo.', 'We could not recover the order’s safe submission state. We did not send it to avoid a duplicate.'), 'error');
      return;
    }
    setSubmitting(true);
    let submission = pendingSubmissionRef.current;
    if (!submission) {
      const idempotencyKey = typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `order-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
      const request = {
        items: cart.map(item => ({
          product: item._id,
          quantity: item.quantity,
          price: item.price,
          selectedOptions: item.selectedOptions
        })),
        paymentMethod: method,
        delivery: {
          customerName: deliveryData.customerName.trim(),
          phone: deliveryData.phone.replace(/\s/g, ''),
          address: deliveryData.address.trim(),
          addressNotes: deliveryData.addressNotes.trim() || undefined,
          coordinates: deliveryData.coordinates || undefined
        },
        couponCode: appliedCoupon?.code || undefined,
        idempotencyKey
      };
      try {
        submission = savePendingOrderSubmission({
          idempotencyKey,
          request,
          cartSnapshot: cart,
          deliverySnapshot: deliveryData,
          couponSnapshot: appliedCoupon,
        });
        pendingSubmissionRef.current = submission;
      } catch (error) {
        console.error('Could not persist pending order submission:', error);
        showToast(tr('No se pudo guardar el envío en este dispositivo. No enviamos el pedido; libera espacio o revisa el almacenamiento e intenta de nuevo.', 'We could not save the submission on this device. Your order was not sent. Free up space or check storage, then try again.'), 'error');
        setSubmitting(false);
        return;
      }
    }
    try {
      const res = await axios.post(`${API_URL}/orders`, submission.request);

      // The accepted server response is authoritative: persist its line prices
      // and total, and tell the customer clearly if the accepted total differs
      // from what the local cart displayed (e.g. a price changed after the
      // menu loaded).
      const acceptedTotal: number = res.data?.totalAmount ?? finalTotal;
      if (Math.abs(acceptedTotal - finalTotal) > 0.001) {
        showToast(tr(`El total aceptado fue $${acceptedTotal.toLocaleString()} (precios actualizados).`, `The accepted total was $${acceptedTotal.toLocaleString()} (prices were updated).`), 'info');
      }

      const historyEntry: OrderHistoryEntry = {
        items: (res.data?.items || cart).map((item: any) => ({
          productId: item.product?._id || item.product || item._id,
          name: item.name || item.product?.name,
          quantity: item.quantity,
          price: item.price,
          selectedOptions: item.selectedOptions instanceof Map ? Object.fromEntries(item.selectedOptions) : (item.selectedOptions || {})
        })),
        // Minimise stored PII: keep only what reorder needs; precise location
        // and notes are never persisted on a shared device.
        delivery: {
          customerName: submission.deliverySnapshot.customerName,
          phone: submission.deliverySnapshot.phone,
          address: submission.deliverySnapshot.address,
          addressNotes: '',
          coordinates: null
        },
        date: new Date().toISOString(),
        total: acceptedTotal,
        orderNumber: res.data?.orderNumber,
        paymentMethod: submission.request.paymentMethod,
        paymentStatus: submission.request.paymentMethod === 'Transferencia'
          ? 'Pendiente de verificación'
          : 'Pendiente al recibir el pedido',
        paymentInstructions: submission.request.paymentMethod === 'Transferencia'
          ? 'Sigue las instrucciones de pago del restaurante; esta aplicación no realiza la transferencia.'
          : 'Paga en efectivo al recibir el pedido.',
        orderStatus: res.data?.status || 'pendiente'
      };
      saveOrderToHistory(historyEntry);
      setOrderHistory(loadOrderHistory());
      clearCheckoutDraft();
      try {
        clearPendingOrderSubmission();
      } catch (error) {
        // Retaining a completed key is safe: a later retry returns this order.
        console.error('Could not clear completed pending order submission:', error);
      }
      pendingSubmissionRef.current = null;

      setOrderReceipt({
        orderNumber: res.data?.orderNumber,
        total: acceptedTotal,
        paymentMethod: submission.request.paymentMethod,
        orderStatus: res.data?.status || 'pendiente'
      });
      setCart([]);
      setIsCartOpen(false);
      setCheckoutStep(1);
      // Deliberately keep deliveryData filled (not reset) so a customer
      // placing a second order in the same visit doesn't have to retype it.
      setTouched({});
      setFieldErrors({});
      setAppliedCoupon(null);
      setCouponInput('');
      setCouponError('');
      setShowCouponField(false);
    } catch (error: any) {
      const message = error.response?.data?.message
        ? localizeApiMessage(error.response.data.message, language)
        : tr('Error al realizar el pedido', 'There was an error placing your order.');
      if (error.response?.status >= 400 && error.response?.status < 500) {
        try {
          clearPendingOrderSubmission();
          pendingSubmissionRef.current = null;
        } catch (storageError) {
          console.error('Could not clear rejected pending order submission:', storageError);
        }
      }
      // Un producto se deshabilitó después de cargar el menú: quítalo del carrito
      // para que el cliente pueda reintentar sin el ítem agotado.
      const unavailableId = error.response?.data?.unavailableProduct;
      if (error.response?.status === 409 && unavailableId) {
        setCart((prev: any[]) => prev.filter((item: any) => item._id !== unavailableId));
      }
      if (!error.response) {
        // Unknown outcome: the request never got an answer, so the order may
        // exist. The cart is kept and the retry reuses the idempotency key,
        // which makes the retry safe. Never claim the order failed.
        showToast(tr('No pudimos confirmar tu pedido (conexión interrumpida). Tu pedido puede haberse recibido: espera unos segundos y toca "Realizar pedido" de nuevo — no se creará por duplicado.', 'We could not confirm your order because the connection was interrupted. It may have been received. Wait a few seconds and select “Place order” again; a duplicate will not be created.'), 'error');
      } else {
        showToast(message, 'error');
      }
      console.error('Order error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl min-h-screen bg-background-light dark:bg-background-dark pb-24 transition-colors duration-300">
      {!isOnline && (
        <div role="status" className="bg-red-500 text-white text-center text-sm font-bold px-6 py-2">
          {tr('Sin conexión — no se pueden enviar pedidos. Tu carrito está guardado en este dispositivo.', 'Offline — orders cannot be submitted. Your cart is saved on this device.')}
        </div>
      )}
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-black/80 backdrop-blur-md px-6 py-4 border-b border-slate-100 dark:border-slate-800">
        <div className="flex justify-between items-center mb-4">
          <div className="flex items-center gap-3">
            <BrandLogo />
            <div>
              <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-white">CHORIPAN <span className="text-primary">NEA</span></h1>
              <p className="text-xs text-slate-500 font-medium uppercase tracking-widest">Premium Street Food</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex rounded-full border border-slate-200 dark:border-slate-700 p-1" role="group" aria-label={tr('Idioma de la página', 'Page language')}>
              {(['es', 'en'] as CustomerLanguage[]).map(option => (
                <button
                  key={option}
                  type="button"
                  lang={option}
                  aria-pressed={language === option}
                  onClick={() => setLanguage(option)}
                  className={`px-3 py-1.5 rounded-full text-xs font-bold transition-colors ${language === option ? 'bg-primary text-white' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'}`}
                >
                  {option === 'es' ? 'Español' : 'English'}
                </button>
              ))}
            </div>
            <ThemeToggle />
          </div>
        </div>

        {/* Search Bar */}
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input 
            type="text" 
            aria-label={tr('Buscar productos del menú', 'Search menu items')}
            placeholder={tr('¿Qué se te antoja hoy?', 'What are you craving today?')}
            className="w-full bg-slate-100 dark:bg-slate-900 border-none rounded-2xl py-3 pl-10 pr-4 text-sm focus:ring-2 focus:ring-primary/20 transition-all"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Categories Scroll */}
        <div className="flex gap-2 overflow-x-auto no-scrollbar pb-2">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`
                px-5 py-2 rounded-full text-sm font-bold whitespace-nowrap transition-all
                ${activeCategory === cat 
                  ? 'bg-primary text-white shadow-lg shadow-primary/20 scale-105' 
                  : 'bg-slate-100 dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:bg-slate-200'}
              `}
            >
              {cat === 'Todos' ? tr('Todos', 'All') : cat === 'Bebidas' ? tr('Bebidas', 'Drinks') : cat === 'Combos' ? tr('Combos', 'Combos') : cat}
            </button>
          ))}
        </div>
      </header>

      {/* Order History */}
      {orderHistory.length > 0 && (
        <section className="px-6 pt-6">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-between w-full mb-4 group"
          >
            <div className="flex items-center gap-2">
              <RotateCcw size={16} className="text-primary" />
              <span className="font-black text-sm text-slate-700 dark:text-slate-200">{tr('Tus pedidos anteriores', 'Your previous orders')}</span>
              <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full font-bold">{orderHistory.length}</span>
            </div>
            <ChevronDown size={16} className={`text-slate-400 transition-transform ${showHistory ? 'rotate-180' : ''}`} />
          </button>
          {showHistory && (
            <button
              onClick={() => {
                if (window.confirm(tr('¿Borrar tu historial de pedidos de este dispositivo?', 'Clear your order history from this device?'))) {
                  clearOrderHistory();
                  setOrderHistory([]);
                  showToast(tr('Historial borrado', 'Order history cleared'), 'success');
                }
              }}
              className="text-[11px] font-bold text-slate-400 hover:text-red-500 mb-3 block"
            >
              {tr('Borrar historial de este dispositivo', 'Clear history from this device')}
            </button>
          )}
          <AnimatePresence>
            {showHistory && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden"
              >
                <div className="space-y-3 pb-4">
                  {orderHistory.map((entry, idx) => (
                    <div key={idx} className="bg-white dark:bg-slate-900 rounded-2xl p-4 border border-slate-100 dark:border-slate-800 shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-[10px] text-slate-400 font-bold uppercase">
                            {relativeDate(entry.date, language)}
                            {entry.orderNumber && <span className="text-primary ml-2">#{entry.orderNumber}</span>}
                          </p>
                          <p className="text-sm font-bold text-slate-700 dark:text-slate-200 truncate mt-0.5">
                            {entry.items.map(i => `${i.quantity}x ${i.name}`).join(', ')}
                          </p>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {entry.items.flatMap(i =>
                              Object.values(i.selectedOptions || {}).map((v, j) => (
                                <span key={`${i.productId}-${j}`} className="text-[10px] bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded-full text-slate-500 font-bold">{v}</span>
                              ))
                            )}
                          </div>
                          {entry.paymentMethod && (
                            <div className="text-[11px] font-bold text-slate-500 dark:text-slate-400 mt-2 space-y-0.5">
                              <p>{tr('Estado del pedido:', 'Order status:')} {localizeStatus(entry.orderStatus || 'Recibido', language)}</p>
                              <p>{tr('Pago:', 'Payment:')} {localizePayment(entry.paymentMethod || '', language)} · {entry.paymentStatus ? localizeStatus(entry.paymentStatus, language) : tr('Estado pendiente de confirmación', 'Awaiting confirmation')}</p>
                              {entry.paymentInstructions && <p>{localizePaymentInstructions(entry.paymentInstructions, language)}</p>}
                            </div>
                          )}
                        </div>
                        <span className="font-black text-primary text-sm ml-3">${entry.total.toLocaleString()}</span>
                      </div>
                      <button
                        onClick={() => reorderFromHistory(entry)}
                        className="w-full mt-2 py-2.5 rounded-xl bg-primary/10 hover:bg-primary/20 text-primary font-black text-xs transition-colors flex items-center justify-center gap-2"
                      >
                        <RotateCcw size={14} />
                        {tr('Repetir pedido', 'Order again')}
                      </button>
                    </div>
                  ))}
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </section>
      )}

      {/* Menu Items */}
      <main className="p-6 space-y-6">
        {menuError && products.length > 0 && (
          <div role="alert" className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
            <p className="font-bold">{tr('No pudimos actualizar el menú. Mostramos los productos que ya estaban cargados.', 'We could not refresh the menu. Showing the products that were already loaded.')}</p>
            <Button onClick={fetchProducts} disabled={loading} className="mt-3 rounded-full">
              {loading ? tr('Actualizando…', 'Refreshing…') : tr('Reintentar', 'Try again')}
            </Button>
          </div>
        )}
        {loading && products.length === 0 ? (
          [1, 2, 3, 4].map(i => (
            <div key={i} className="flex gap-4">
              <Skeleton className="h-24 w-24 flex-shrink-0" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-4 w-full" />
                <Skeleton className="h-6 w-20" />
              </div>
            </div>
          ))
        ) : menuError && products.length === 0 ? (
          <div role="alert" className="text-center py-14 px-6 bg-white dark:bg-slate-900 rounded-3xl border border-red-200 dark:border-red-900">
            <p className="text-4xl mb-4" aria-hidden="true">📡</p>
            <h2 className="font-black text-xl mb-2 dark:text-white">{tr('No pudimos cargar el menú', 'We could not load the menu')}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
              {tr('Revisa tu conexión e inténtalo de nuevo. Tu carrito y los datos que ingresaste siguen guardados.', 'Check your connection and try again. Your cart and the details you entered are still saved.')}
            </p>
            <Button onClick={fetchProducts} disabled={loading} className="rounded-full">
              {loading ? tr('Cargando…', 'Loading…') : tr('Reintentar', 'Try again')}
            </Button>
          </div>
        ) : products.length === 0 ? (
          <div role="status" className="text-center py-14 px-6 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800">
            <p className="text-4xl mb-4" aria-hidden="true">🌭</p>
            <h2 className="font-black text-xl mb-2 dark:text-white">{tr('El menú está vacío por ahora', 'The menu is empty right now')}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
              {tr('Vuelve a intentarlo más tarde para ver los productos disponibles.', 'Come back later to see available items.')}
            </p>
            <Button onClick={fetchProducts} disabled={loading} className="rounded-full">
              {loading ? tr('Cargando…', 'Loading…') : tr('Actualizar menú', 'Refresh menu')}
            </Button>
          </div>
        ) : filteredProducts.length === 0 ? (
          <div className="text-center py-14 px-6 bg-white dark:bg-slate-900 rounded-3xl border border-slate-100 dark:border-slate-800">
            <p className="text-4xl mb-4">🌭</p>
            <h2 className="font-black text-xl mb-2 dark:text-white">{tr('No encontramos ese antojo', 'We could not find that item')}</h2>
            <p className="text-sm text-slate-500 dark:text-slate-400 mb-5">
              {tr('Prueba otra búsqueda o vuelve a todos los productos disponibles.', 'Try another search or browse all available items.')}
            </p>
            {(searchQuery || activeCategory !== 'Todos') && (
              <Button onClick={() => { setSearchQuery(''); setActiveCategory('Todos'); }} className="rounded-full">
                {tr('Ver todo el menú', 'View the full menu')}
              </Button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6">
            <AnimatePresence mode='popLayout'>
              {filteredProducts.map(product => (
                <motion.div
                  layout
                  key={product._id}
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.9 }}
                >
                  <Card className="flex gap-4 p-3 items-start">
                    <>
                      <div className="h-20 w-20 sm:h-28 sm:w-28 bg-slate-100 dark:bg-slate-800 rounded-xl flex items-center justify-center text-3xl overflow-hidden flex-shrink-0">
                        {product.imageUrl ? (
                          <img src={resolveProductImageUrl(product.imageUrl)} alt={product.name} className="h-full w-full object-cover" loading="lazy" />
                        ) : (
                          getProductFallbackIcon(product)
                        )}
                      </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="font-bold text-lg truncate">{product.name}</h3>
                          <p className="text-slate-500 dark:text-slate-400 text-xs line-clamp-2 mt-1">{product.description}</p>
                        </div>
                        {product.options?.length > 0 && (
                          <span className="text-[10px] font-black uppercase tracking-wider bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300 px-2 py-1 rounded-full whitespace-nowrap">
                            {tr('Personalizable', 'Customizable')}
                          </span>
                        )}
                      </div>

                      {product.options?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-3">
                          {product.options.slice(0, 3).map((opt: any) => (
                            <span key={opt.name} className="text-[10px] bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-full text-slate-500 font-bold">
                              {opt.name}
                            </span>
                          ))}
                        </div>
                      )}

                      <div className="mt-5 pt-4 border-t border-slate-100 dark:border-slate-800 flex flex-wrap gap-2 justify-between items-center">
                        <span className="text-primary font-black text-lg">${product.price.toLocaleString()}</span>
                        {product.options?.length > 0 ? (
                          <Button
                            size="sm"
                            className="rounded-full px-4 font-black"
                            onClick={() => openProductBuilder(product)}
                          >
                            {tr('Personalizar', 'Customize')}
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            className="rounded-full h-10 w-10 p-0"
                            onClick={() => addToCart(product)}
                          >
                            <Plus size={20} />
                          </Button>
                        )}
                      </div>
                    </div>
                    </>
                  </Card>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </main>

      {/* Product Customization Builder */}
      <AnimatePresence>
        {builderProduct && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={closeProductBuilder}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 26, stiffness: 210 }}
              className="fixed inset-x-0 bottom-0 mx-auto max-w-3xl bg-white dark:bg-slate-950 z-50 rounded-t-[32px] max-h-[92vh] overflow-y-auto"
            >
              <div className="sticky top-0 bg-white/95 dark:bg-slate-950/95 backdrop-blur-md px-6 pt-5 pb-4 border-b border-slate-100 dark:border-slate-800 z-10">
                <div className="w-12 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full mx-auto mb-5" />
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-primary mb-1">{tr('Arma tu choripan', 'Build your choripan')}</p>
                    <h2 className="text-2xl font-black dark:text-white">{builderProduct.name}</h2>
                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">{builderProduct.description}</p>
                  </div>
                  <button
                    type="button"
                    onClick={closeProductBuilder}
                    className="h-10 w-10 flex items-center justify-center rounded-full bg-slate-100 dark:bg-slate-900 text-slate-500"
                  >
                    <X size={20} />
                  </button>
                </div>
              </div>

              <div className="p-6 space-y-5">
                {builderProduct.options?.map((opt: any, index: number) => {
                  const isGrid = opt.displayStyle === 'grid';
                  const hasIcons = opt.icons && Object.keys(opt.icons).length > 0;
                  const selectedChoice = builderSelections[opt.name];
                  const description = selectedChoice && opt.descriptions?.[selectedChoice];

                  return (
                    <section
                      key={opt.name}
                      className={`p-4 rounded-3xl border ${
                        isGrid
                          ? 'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-900'
                          : 'bg-slate-50 dark:bg-slate-900/50 border-slate-100 dark:border-slate-800'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3 mb-3">
                        <div>
                          <p className={`text-[10px] font-black uppercase tracking-widest ${isGrid ? 'text-orange-600 dark:text-orange-400' : 'text-primary'}`}>
                            {tr('Paso', 'Step')} {index + 1}
                          </p>
                          <h3 className="text-lg font-black dark:text-white">{tr('Elige', 'Choose')} {opt.name}</h3>
                        </div>
                        {selectedChoice && (
                          <span className="text-[10px] font-black bg-white dark:bg-slate-800 text-slate-500 px-2 py-1 rounded-full">
                            {selectedChoice}
                          </span>
                        )}
                      </div>

                      {isGrid ? (
                        <div className="grid gap-2" style={{ gridTemplateColumns: `repeat(${Math.min(opt.choices.length, 3)}, 1fr)` }}>
                          {opt.choices.map((choice: string) => {
                            const isSelected = selectedChoice === choice;
                            const icon = hasIcons ? opt.icons[choice] : null;

                            return (
                              <button
                                key={choice}
                                type="button"
                                onClick={() => updateBuilderSelection(opt.name, choice)}
                                className={`
                                  flex flex-col items-center justify-center p-3 rounded-2xl transition-all border-2 min-h-[88px]
                                  ${isSelected
                                    ? 'bg-orange-500 border-orange-500 shadow-lg shadow-orange-500/30 scale-105'
                                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 hover:border-orange-300 active:scale-95'}
                                `}
                              >
                                {icon && (
                                  icon.startsWith('http')
                                    ? <img src={icon} alt={choice} className="h-8 w-8 object-contain mb-1" />
                                    : <span className="text-2xl mb-1">{icon}</span>
                                )}
                                <span className={`text-xs font-black leading-tight ${isSelected ? 'text-white' : 'text-slate-700 dark:text-slate-200'}`}>
                                  {choice}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      ) : (
                        <div className="flex flex-wrap gap-2">
                          {opt.choices.map((choice: string) => {
                            const isSelected = selectedChoice === choice;
                            const icon = hasIcons ? opt.icons[choice] : null;

                            return (
                              <button
                                key={choice}
                                type="button"
                                onClick={() => updateBuilderSelection(opt.name, choice)}
                                className={`
                                  px-4 py-2.5 rounded-full text-xs font-bold transition-all border-2
                                  ${isSelected
                                    ? 'bg-primary border-primary text-white shadow-md'
                                    : 'bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-primary/50 active:scale-95'}
                                `}
                              >
                                {icon && (
                                  icon.startsWith('http')
                                    ? <img src={icon} alt={choice} className="h-4 w-4 object-contain inline mr-1" />
                                    : <span className="mr-1">{icon}</span>
                                )}{choice}
                              </button>
                            );
                          })}
                        </div>
                      )}

                      {description && (
                        <motion.p
                          initial={{ opacity: 0, y: -5 }}
                          animate={{ opacity: 1, y: 0 }}
                          className={`text-[11px] font-medium mt-3 leading-relaxed ${isGrid ? 'text-orange-700 dark:text-orange-300' : 'text-slate-500 dark:text-slate-400'}`}
                        >
                          {description}
                        </motion.p>
                      )}
                    </section>
                  );
                })}

                <div className="sticky bottom-0 -mx-6 px-6 pt-4 pb-6 bg-white/95 dark:bg-slate-950/95 backdrop-blur-md border-t border-slate-100 dark:border-slate-800">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <p className="text-[10px] uppercase font-bold text-slate-400 leading-none">{tr('Total producto', 'Item total')}</p>
                      <p className="text-2xl font-black text-primary">${builderProduct.price.toLocaleString()}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-[10px] uppercase font-bold text-slate-400 leading-none">{tr('Selecciones', 'Selections')}</p>
                      <p className="text-sm font-black dark:text-white">
                        {Object.values(builderSelections).filter(Boolean).length}/{builderProduct.options?.length || 0}
                      </p>
                    </div>
                  </div>
                  <Button onClick={addBuilderProductToCart} className="w-full h-14 text-lg font-black rounded-2xl">
                    {tr('Agregar al carrito', 'Add to cart')}
                    <Plus size={20} className="ml-2" />
                  </Button>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Floating Cart Button */}
      <AnimatePresence>
        {cart.length > 0 && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-8 left-0 right-0 mx-auto max-w-3xl px-6 z-40"
          >
            <button 
              onClick={openCheckout}
              className="w-full bg-slate-900 dark:bg-white text-white dark:text-black py-4 rounded-2xl shadow-2xl flex items-center justify-between px-6 group"
            >
              <div className="flex items-center gap-3">
                <div className="bg-primary p-2 rounded-lg text-white">
                  <ShoppingCart size={20} />
                </div>
                <div className="text-left">
                  <p className="text-[10px] uppercase font-bold opacity-60 leading-none">{tr('Tu Carrito', 'Your Cart')}</p>
                  <p className="font-bold">{cart.length} {cart.length === 1 ? tr('ítem', 'item') : tr('ítems', 'items')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-black text-lg">${total.toLocaleString()}</span>
                <ChevronRight size={20} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Checkout Modal (3 steps) */}
      <AnimatePresence>
        {isCartOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsCartOpen(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
            />
            <motion.div 
              initial={{ y: '100%' }}
              animate={{ y: 0 }}
              exit={{ y: '100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-x-0 bottom-0 mx-auto max-w-3xl bg-white dark:bg-slate-950 z-50 rounded-t-[32px] p-6 sm:p-8 max-h-[92vh] overflow-y-auto"
            >
              <div className="w-12 h-1.5 bg-slate-200 dark:bg-slate-800 rounded-full mx-auto mb-6" />

              {/* Step indicator */}
              <div className="flex items-center gap-2 mb-6">
                {[1, 2, 3].map(s => (
                  <div key={s} className="flex items-center gap-2 flex-1">
                    <div className={`h-1.5 rounded-full flex-1 transition-colors ${s <= checkoutStep ? 'bg-primary' : 'bg-slate-200 dark:bg-slate-800'}`} />
                  </div>
                ))}
              </div>

              {/* ========== STEP 1: Cart ========== */}
              {checkoutStep === 1 && (
                <>
                  <div className="flex justify-between items-center mb-6">
                    <h2 className="text-2xl font-black">{tr('Tu Pedido', 'Your Order')}</h2>
                    <Button variant="ghost" size="sm" onClick={() => setIsCartOpen(false)}>{tr('Cerrar', 'Close')}</Button>
                  </div>

                  <div className="space-y-4 mb-8">
                    {cart.map(item => {
                      const optionsMeta = item.options || [];
                      return (
                        <div key={item.cartItemId} className="flex justify-between items-center">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-bold truncate">{item.name}</h4>
                            <div className="flex flex-wrap gap-1 mb-1">
                              {Object.entries(item.selectedOptions || {}).map(([key, val]) => {
                                const optMeta = optionsMeta.find((o: any) => o.name === key);
                                const isGrid = optMeta?.displayStyle === 'grid';
                                const icon = optMeta?.icons?.[val as string];
                                return (
                                  <span key={key} className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${isGrid ? 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300' : 'bg-slate-100 dark:bg-slate-800 text-slate-500'}`}>
                                    {icon && icon.startsWith('http')
                                      ? <img src={icon} alt={val as string} className="h-3 w-3 object-contain inline mr-1" />
                                      : icon ? <span className="mr-0.5">{icon}</span> : null
                                    }{val as string}
                                  </span>
                                );
                              })}
                            </div>
                            <p className="text-sm text-slate-500">${item.price.toLocaleString()}</p>
                          </div>
                          <div className="flex items-center gap-3 bg-slate-100 dark:bg-slate-900 rounded-2xl p-1.5">
                            <button onClick={() => removeFromCart(item.cartItemId)} className="h-8 w-8 flex items-center justify-center bg-white dark:bg-slate-800 rounded-xl shadow-sm">
                              <Minus size={14} />
                            </button>
                            <span className="font-black w-4 text-center text-sm">{item.quantity}</span>
                            <button onClick={() => addToCart(item)} className="h-8 w-8 flex items-center justify-center bg-primary text-white rounded-xl shadow-sm">
                              <Plus size={14} />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-5 mb-6">
                    <div className="flex justify-between items-center">
                      <span className="font-bold">{tr('Total', 'Total')}</span>
                      <span className="text-xl font-black text-primary">${total.toLocaleString()}</span>
                    </div>
                  </div>

                  <Button onClick={() => setCheckoutStep(2)} className="w-full h-14 text-lg font-black rounded-2xl">
                    {tr('Continuar', 'Continue')}
                    <ChevronRight size={20} className="ml-2" />
                  </Button>
                </>
              )}

              {/* ========== STEP 2: Delivery Form ========== */}
              {checkoutStep === 2 && (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    <button onClick={() => setCheckoutStep(1)} className="h-10 w-10 flex items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-900">
                      <ChevronLeft size={20} />
                    </button>
                    <h2 className="text-2xl font-black">{tr('Datos de Envío', 'Delivery Details')}</h2>
                  </div>

                  <form noValidate onSubmit={event => { event.preventDefault(); confirmDeliveryDetails(); }}>
                  <div className="space-y-4 mb-6">
                    {/* Name */}
                    <div>
                      <label htmlFor="delivery-customer-name" className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5">
                        <User size={12} /> {tr('Nombre (obligatorio)', 'Name (required)')}
                      </label>
                      <input
                        ref={customerNameInputRef}
                        id="delivery-customer-name"
                        type="text"
                        autoComplete="name"
                        required
                        aria-required="true"
                        aria-invalid={Boolean(touched.customerName && fieldErrors.customerName)}
                        aria-describedby={touched.customerName && fieldErrors.customerName ? 'delivery-customer-name-error' : 'delivery-customer-name-hint'}
                        value={deliveryData.customerName}
                        onChange={e => handleDeliveryChange('customerName', e.target.value)}
                        onBlur={() => handleBlur('customerName')}
                        placeholder={tr('Tu nombre', 'Your name')}
                        className={`w-full rounded-xl px-4 py-3 text-sm bg-slate-50 dark:bg-slate-900 border-2 transition-colors outline-none ${
                          touched.customerName
                            ? fieldErrors.customerName ? 'border-red-400 focus:border-red-500' : 'border-green-400 focus:border-green-500'
                            : 'border-transparent focus:border-primary'
                        }`}
                      />
                      <p id="delivery-customer-name-hint" className="sr-only">{tr('Campo obligatorio.', 'Required field.')}</p>
                      {touched.customerName && fieldErrors.customerName && (
                        <p id="delivery-customer-name-error" className="text-[11px] text-red-500 font-bold mt-1">{fieldErrors.customerName}</p>
                      )}
                    </div>

                    {/* Phone */}
                    <div>
                      <label htmlFor="delivery-phone" className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5">
                        <Phone size={12} /> {tr('Teléfono (obligatorio)', 'Phone (required)')}
                      </label>
                      <input
                        ref={phoneInputRef}
                        id="delivery-phone"
                        type="tel"
                        inputMode="numeric"
                        autoComplete="tel-national"
                        required
                        aria-required="true"
                        aria-invalid={Boolean(touched.phone && fieldErrors.phone)}
                        aria-describedby={touched.phone && fieldErrors.phone ? 'delivery-phone-error' : 'delivery-phone-hint'}
                        value={deliveryData.phone}
                        onChange={e => handleDeliveryChange('phone', e.target.value)}
                        onBlur={() => handleBlur('phone')}
                        placeholder="3XX XXX XXXX"
                        maxLength={12}
                        className={`w-full rounded-xl px-4 py-3 text-sm bg-slate-50 dark:bg-slate-900 border-2 transition-colors outline-none ${
                          touched.phone
                            ? fieldErrors.phone ? 'border-red-400 focus:border-red-500' : 'border-green-400 focus:border-green-500'
                            : 'border-transparent focus:border-primary'
                        }`}
                      />
                      <p id="delivery-phone-hint" className="sr-only">{tr('Campo obligatorio. Ingresa un celular colombiano de 10 dígitos.', 'Required field. Enter a 10-digit Colombian mobile number.')}</p>
                      {touched.phone && fieldErrors.phone && (
                        <p id="delivery-phone-error" className="text-[11px] text-red-500 font-bold mt-1">{fieldErrors.phone}</p>
                      )}
                    </div>

                    {/* Address with map */}
                    <div>
                      <label htmlFor="delivery-address" className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5">
                        <MapPin size={12} /> {tr('Dirección (obligatoria)', 'Address (required)')}
                      </label>
                      <div className="relative">
                        <input
                          ref={addressInputRef}
                          id="delivery-address"
                          type="text"
                          autoComplete="street-address"
                          required
                          role="combobox"
                          aria-autocomplete="list"
                          aria-haspopup="listbox"
                          aria-expanded={addressSuggestions.length > 0}
                          aria-controls="delivery-address-suggestions"
                          aria-activedescendant={addressSuggestions.length > 0 ? `delivery-address-option-${activeSuggestionIndex}` : undefined}
                          aria-invalid={Boolean(touched.address && fieldErrors.address)}
                          aria-describedby={touched.address && fieldErrors.address ? 'delivery-address-error' : 'delivery-address-hint'}
                          value={deliveryData.address}
                          onChange={e => handleDeliveryChange('address', e.target.value)}
                          onBlur={() => { setTimeout(() => setAddressSuggestions([]), 200); handleBlur('address'); }}
                          onKeyDown={handleAddressKeyDown}
                          placeholder={tr('Escribe o selecciona en el mapa', 'Type an address or select it on the map')}
                          className={`w-full rounded-xl px-4 py-3 text-sm bg-slate-50 dark:bg-slate-900 border-2 transition-colors outline-none ${
                            touched.address
                              ? fieldErrors.address ? 'border-red-400 focus:border-red-500' : 'border-green-400 focus:border-green-500'
                              : 'border-transparent focus:border-primary'
                          }`}
                        />
                        {addressSuggestions.length > 0 && (
                          <div id="delivery-address-suggestions" role="listbox" aria-label={tr('Sugerencias de dirección', 'Address suggestions')} className="absolute z-10 left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden">
                            {addressSuggestions.map((s: any, i: number) => (
                              <div
                                key={i}
                                id={`delivery-address-option-${i}`}
                                role="option"
                                aria-selected={activeSuggestionIndex === i}
                                onMouseDown={event => event.preventDefault()}
                                onClick={() => selectSuggestion(s)}
                                className={`w-full text-left px-4 py-3 text-xs cursor-pointer border-b border-slate-100 dark:border-slate-800 last:border-0 transition-colors ${activeSuggestionIndex === i ? 'bg-slate-100 dark:bg-slate-800' : 'hover:bg-slate-50 dark:hover:bg-slate-800'}`}
                              >
                                <MapPin size={12} className="inline mr-2 text-primary" />
                                {s.display_name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      <p id="delivery-address-hint" className="text-[10px] text-slate-400 mt-1">{tr('Campo obligatorio. Puedes escribir una dirección aunque las sugerencias no estén disponibles.', 'Required field. You can type an address even if suggestions are unavailable.')}</p>
                      {touched.address && fieldErrors.address && (
                        <p id="delivery-address-error" className="text-[11px] text-red-500 font-bold mt-1">{fieldErrors.address}</p>
                      )}
                    </div>

                    {/* Map */}
                    <div className="flex items-start gap-2 rounded-2xl bg-primary/10 text-primary p-3">
                      <MapPin size={16} className="mt-0.5 flex-shrink-0" />
                      <p className="text-[11px] font-bold leading-relaxed">
                        {tr('El pin ayuda al domiciliario, pero puedes continuar solo con una dirección clara.', 'The pin helps your courier, but you can continue with a clear address alone.')}
                      </p>
                    </div>
                    <div className="rounded-2xl overflow-hidden border-2 border-slate-100 dark:border-slate-800 relative" style={{ height: 220 }}>
                      <MapContainer center={mapCenter} zoom={15} scrollWheelZoom={false} style={{ height: '100%', width: '100%' }} zoomControl={false}>
                        <TileLayer
                          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OSM</a>'
                          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <DraggableMarker position={markerPos} onMove={handleMarkerMove} />
                        <RecenterMap lat={mapCenter[0]} lng={mapCenter[1]} />
                      </MapContainer>
                      <button
                        type="button"
                        onClick={requestGeolocation}
                        disabled={geoLoading}
                        aria-label={geoLoading ? tr('Obteniendo ubicación actual', 'Getting your current location') : tr('Usar mi ubicación actual en el mapa', 'Use my current location on the map')}
                        className="absolute bottom-3 right-3 z-[1000] bg-white dark:bg-slate-800 shadow-lg rounded-xl p-2.5 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 transition-colors"
                      >
                        <Navigation size={18} className={`text-primary ${geoLoading ? 'animate-spin' : ''}`} />
                      </button>
                    </div>

                    {/* Address Notes */}
                    <div>
                      <label htmlFor="delivery-address-notes" className="text-[11px] font-black uppercase tracking-widest text-slate-500 mb-1.5 flex items-center gap-1.5">
                        <FileText size={12} /> {tr('Notas', 'Notes')} <span className="font-normal normal-case">({tr('opcional', 'optional')})</span>
                      </label>
                      <input
                        id="delivery-address-notes"
                        type="text"
                        autoComplete="address-line2"
                        aria-describedby="delivery-address-notes-hint"
                        value={deliveryData.addressNotes}
                        onChange={e => setDeliveryData(prev => ({ ...prev, addressNotes: e.target.value.slice(0, 100) }))}
                        placeholder={tr('Apto, piso, color de casa...', 'Apartment, floor, house color...')}
                        className="w-full rounded-xl px-4 py-3 text-sm bg-slate-50 dark:bg-slate-900 border-2 border-transparent focus:border-primary outline-none transition-colors"
                      />
                      <p id="delivery-address-notes-hint" className="text-[10px] text-slate-400 text-right mt-1">{tr('Opcional', 'Optional')} · {deliveryData.addressNotes.length} {tr('caracteres', 'characters')}.</p>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    className={`w-full h-14 text-lg font-black rounded-2xl transition-opacity ${!isDeliveryValid() ? 'opacity-70' : ''}`}
                  >
                    {tr('Confirmar Datos', 'Confirm Details')}
                    <ChevronRight size={20} className="ml-2" />
                  </Button>
                  </form>
                </>
              )}

              {/* ========== STEP 3: Summary + Pay ========== */}
              {checkoutStep === 3 && (
                <>
                  <div className="flex items-center gap-3 mb-6">
                    {!pendingSubmissionRef.current && (
                      <button onClick={() => setCheckoutStep(2)} className="h-10 w-10 flex items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-900">
                        <ChevronLeft size={20} />
                      </button>
                    )}
                    <h2 className="text-2xl font-black">{tr('Confirmar Pedido', 'Confirm Order')}</h2>
                  </div>

                  {/* Delivery summary */}
                  <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-5 mb-6 space-y-3">
                    <p className="text-[10px] font-black uppercase tracking-widest text-primary mb-2">{tr('Datos de envío', 'Delivery details')}</p>
                    <div className="flex items-center gap-3">
                      <User size={16} className="text-slate-400 flex-shrink-0" />
                      <span className="font-bold text-sm">{deliveryData.customerName}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <Phone size={16} className="text-slate-400 flex-shrink-0" />
                      <span className="text-sm">{deliveryData.phone}</span>
                    </div>
                    <div className="flex items-start gap-3">
                      <MapPin size={16} className="text-slate-400 flex-shrink-0 mt-0.5" />
                      <span className="text-sm leading-snug">{deliveryData.address}</span>
                    </div>
                    {deliveryData.addressNotes && (
                      <div className="flex items-center gap-3">
                        <FileText size={16} className="text-slate-400 flex-shrink-0" />
                        <span className="text-sm text-slate-500">{deliveryData.addressNotes}</span>
                      </div>
                    )}
                  </div>

                  {/* Mini map */}
                  {deliveryData.coordinates && (
                    <div className="rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 mb-6" style={{ height: 120 }}>
                      <MapContainer center={[deliveryData.coordinates.lat, deliveryData.coordinates.lng]} zoom={15} scrollWheelZoom={false} dragging={false} zoomControl={false} style={{ height: '100%', width: '100%' }}>
                        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                        <Marker position={[deliveryData.coordinates.lat, deliveryData.coordinates.lng]} />
                      </MapContainer>
                    </div>
                  )}

                  {/* Order items summary */}
                  <div className="space-y-3 mb-6">
                    <p className="text-[10px] font-black uppercase tracking-widest text-primary">{tr('Tu pedido', 'Your order')}</p>
                    {pendingSubmissionRef.current && (
                      <p role="status" className="rounded-xl bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-xs font-bold text-amber-700 dark:text-amber-300">
                        {tr('Estamos confirmando el mismo pedido. El reintento enviará los datos originales y no creará otro pedido.', 'We are confirming the same order. Retrying will send the original details and will not create another order.')}
                      </p>
                    )}
                    {cart.map(item => (
                      <div key={item.cartItemId} className="flex justify-between items-center text-sm">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-primary">{item.quantity}x</span>
                          <span className="font-bold">{item.name}</span>
                          <div className="flex gap-1">
                            {Object.values(item.selectedOptions || {}).map((val: any, i: number) => (
                              <span key={i} className="text-[10px] bg-slate-100 dark:bg-slate-800 px-2 py-0.5 rounded font-bold text-slate-500">{val}</span>
                            ))}
                          </div>
                        </div>
                        <span className="font-bold">${(item.price * item.quantity).toLocaleString()}</span>
                      </div>
                    ))}
                  </div>

                  {/* Coupon Field */}
                  <div className="mb-6">
                    <button
                      onClick={() => setShowCouponField(!showCouponField)}
                      disabled={!!pendingSubmissionRef.current}
                      className="flex items-center gap-2 text-sm font-bold text-primary hover:text-primary/80 transition-colors"
                    >
                      <Tag size={14} />
                      {showCouponField ? tr('Ocultar cupón', 'Hide coupon') : tr('Tengo un cupón +', 'I have a coupon +')}
                    </button>
                    <AnimatePresence>
                      {showCouponField && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: 'auto', opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          className="overflow-hidden"
                        >
                          <div className="flex gap-2 mt-3">
                            <input
                              type="text"
                              aria-label={tr('Código de cupón', 'Coupon code')}
                              value={couponInput}
                              onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                              placeholder={tr('Código de cupón', 'Coupon code')}
                              disabled={!!appliedCoupon || !!pendingSubmissionRef.current}
                              className={`flex-1 rounded-xl px-4 py-3 text-sm font-bold uppercase bg-slate-50 dark:bg-slate-900 border-2 outline-none transition-colors ${
                                appliedCoupon ? 'border-green-400 bg-green-50 dark:bg-green-950/30' : couponError ? 'border-red-400' : 'border-transparent focus:border-primary'
                              }`}
                            />
                            {appliedCoupon ? (
                              <button
                                onClick={removeCoupon}
                                disabled={!!pendingSubmissionRef.current}
                                className="px-4 py-3 rounded-xl bg-red-100 dark:bg-red-900/30 text-red-600 font-bold text-sm hover:bg-red-200 transition-colors"
                              >
                                {tr('Quitar', 'Remove')}
                              </button>
                            ) : (
                              <button
                                onClick={handleApplyCoupon}
                                disabled={couponLoading || !couponInput.trim() || !!pendingSubmissionRef.current}
                                className="px-5 py-3 rounded-xl bg-primary text-white font-bold text-sm disabled:opacity-40 hover:bg-primary/90 transition-colors"
                              >
                                {couponLoading ? '...' : tr('Aplicar', 'Apply')}
                              </button>
                            )}
                          </div>
                          {couponError && (
                            <p className="text-[11px] text-red-500 font-bold mt-2">{couponError}</p>
                          )}
                          {appliedCoupon && (
                            <motion.p
                              initial={{ opacity: 0, y: -5 }}
                              animate={{ opacity: 1, y: 0 }}
                              className="text-[11px] text-green-600 dark:text-green-400 font-bold mt-2"
                            >
                              {appliedCoupon.description} {tr('aplicado', 'applied')} (-${appliedCoupon.discount.toLocaleString()})
                            </motion.p>
                          )}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-900/50 rounded-2xl p-5 mb-6">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-slate-500 text-sm">{tr('Subtotal', 'Subtotal')}</span>
                      <span className="font-bold">${total.toLocaleString()}</span>
                    </div>
                    {appliedCoupon && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="flex justify-between items-center mb-2"
                      >
                        <span className="text-green-600 dark:text-green-400 text-sm font-bold flex items-center gap-1">
                          <Tag size={12} /> {appliedCoupon.description}
                        </span>
                        <span className="text-green-600 dark:text-green-400 font-bold">-${appliedCoupon.discount.toLocaleString()}</span>
                      </motion.div>
                    )}
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-slate-500 text-sm">{tr('Envío', 'Delivery')}</span>
                      <span className="text-green-500 font-bold text-sm">{tr('Gratis', 'Free')}</span>
                    </div>
                    <div className="h-px bg-slate-200 dark:bg-slate-800 mb-3" />
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold">{tr('Total', 'Total')}</span>
                      <span className="text-2xl font-black text-primary">${finalTotal.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-3">
                    {(!pendingSubmissionRef.current || pendingSubmissionRef.current.request.paymentMethod === 'Efectivo') && (
                      <Button
                        onClick={() => handlePlaceOrder('Efectivo')}
                        disabled={submitting || !pendingSubmissionReady || pendingSubmissionStorageError}
                        className="w-full h-14 text-lg font-black rounded-2xl"
                      >
                        {submitting ? tr('Enviando...', 'Sending...') : pendingSubmissionRef.current ? tr('Reintentar pedido en efectivo', 'Retry cash order') : tr('Pagar en Efectivo', 'Pay with Cash')}
                      </Button>
                    )}
                    {(!pendingSubmissionRef.current || pendingSubmissionRef.current.request.paymentMethod === 'Transferencia') && (
                      <Button
                        variant="secondary"
                        onClick={() => handlePlaceOrder('Transferencia')}
                        disabled={submitting || !pendingSubmissionReady || pendingSubmissionStorageError}
                        className="w-full h-14 text-lg font-black rounded-2xl"
                      >
                        {pendingSubmissionRef.current ? tr('Reintentar pedido por transferencia', 'Retry bank transfer order') : tr('Transferencia / Nequi', 'Bank transfer / Nequi')}
                      </Button>
                    )}
                  </div>
                </>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Persistent accepted-order receipt */}
      <AnimatePresence>
        {orderReceipt && (
          <motion.div 
            role="status"
            aria-live="polite"
            aria-atomic="true"
            initial={{ y: -24, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -24, opacity: 0 }}
            className="fixed top-6 left-4 right-4 mx-auto max-w-[720px] z-50 bg-green-700 text-white p-4 sm:p-5 rounded-2xl shadow-2xl"
          >
            <div className="flex items-start gap-4">
              <div className="h-10 w-10 bg-white/20 rounded-full flex items-center justify-center shrink-0">
                <Clock size={24} aria-hidden="true" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 ref={receiptHeadingRef} tabIndex={-1} className="font-black leading-tight mb-2 focus:outline-none">
                  {tr('Pedido recibido', 'Order received')}{orderReceipt.orderNumber ? ` #${orderReceipt.orderNumber}` : ''}
                </h2>
                <p className="text-sm font-bold">{tr('Total aceptado:', 'Accepted total:')} ${orderReceipt.total.toLocaleString(language === 'es' ? 'es-CO' : 'en-US')} COP</p>
                <p className="text-sm mt-1">{tr('Estado del pedido:', 'Order status:')} {orderReceipt.orderStatus === 'pendiente' ? tr('Recibido, pendiente de preparación', 'Received, awaiting preparation') : localizeStatus(orderReceipt.orderStatus, language)}</p>
                <p className="text-sm mt-1">
                  {orderReceipt.paymentMethod === 'Transferencia'
                    ? tr('Pago por transferencia pendiente de verificación. Esta aplicación no realiza la transferencia; sigue las instrucciones de pago que te dio el restaurante.', 'Bank transfer payment is awaiting verification. This app does not make the transfer; follow the payment instructions from the restaurant.')
                    : tr('Pago en efectivo pendiente al recibir el pedido.', 'Cash payment is due when your order arrives.')}
                </p>
                <p className="text-xs mt-2 opacity-90">{tr('Guarda este comprobante. También está disponible en “Tus pedidos anteriores”.', 'Save this receipt. It is also available under “Your previous orders”.')}</p>
              </div>
              <button
                type="button"
                onClick={() => setOrderReceipt(null)}
                aria-label={tr('Cerrar comprobante del pedido', 'Close order receipt')}
                className="rounded-lg p-2 hover:bg-white/15 focus:outline-none focus:ring-2 focus:ring-white"
              >
                <X size={18} aria-hidden="true" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default MenuQR;
